'use client'

import { useMemo, useState } from 'react'
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form'
import { cx } from 'class-variance-authority'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'

import type { Field } from '@skop-app/lib-domain/types'
import { Button } from '@/components/atoms/Button/button.atom'
import { Checkbox } from '@/components/atoms/Checkbox/checkbox.atom'
import { Input } from '@/components/atoms/Input/input.atom'
import {
  getFriendlyFieldErrorMessage,
  getValueAtPath,
  hasVisibleDependencyValue,
} from './dynamicForm.helpers'
import { DynamicFormFieldRender } from './DynamicFormFieldRender'

type DynamicFormVariants = string
type FieldArrayItem = Record<string, any>

type FieldArrayProps = {
  field: Field
  form: UseFormReturn<any>
  variant?: DynamicFormVariants
}

export const DynamicFormFieldArray = ({
  field,
  form,
  variant,
}: FieldArrayProps) => {
  const { control } = form
  const {
    fields: arrayFields,
    name,
    hideAddBtn,
    hideRemoveBtn,
    checkboxSelectAll,
    fieldsArraySearch,
  } = field
  const [searchTerm, setSearchTerm] = useState('')

  const {
    fields: fieldArrayFields,
    append,
    remove,
  } = useFieldArray<{ [key: string]: FieldArrayItem[] }>({ control, name })

  const watchedFieldArray = useWatch({
    control,
    name,
  })
  const watchedValues = Array.isArray(watchedFieldArray)
    ? watchedFieldArray
    : []
  const controlledRows = useMemo(
    () =>
      fieldArrayFields.map((fieldArrayItem, index) => ({
        ...fieldArrayItem,
        ...watchedValues[index],
      })),
    [fieldArrayFields, watchedValues],
  )

  const filteredRows = useMemo(() => {
    return controlledRows.reduce<
      Array<{ item: FieldArrayItem; originalIndex: number; rowId: string }>
    >((acc, item, originalIndex) => {
      const rowId =
        fieldArrayFields[originalIndex]?.id ?? `${name}-${originalIndex}`
      if (!item) return acc
      if (!searchTerm || !fieldsArraySearch) {
        acc.push({ item, originalIndex, rowId })
        return acc
      }

      const fieldValue = getValueAtPath(item, fieldsArraySearch)
      if (fieldValue == null) return acc

      if (
        fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        acc.push({ item, originalIndex, rowId })
      }

      return acc
    }, [])
  }, [controlledRows, searchTerm, fieldsArraySearch, fieldArrayFields, name])

  const checkboxFieldName = arrayFields?.find(
    (f) => f.type === 'checkbox',
  )?.name

  const getRowCheckboxValue = (item: FieldArrayItem) => {
    if (!checkboxFieldName) return false
    return Boolean(getValueAtPath(item, checkboxFieldName))
  }

  const handleSelectAll = (checked: boolean) => {
    if (!checkboxFieldName) return
    if (controlledRows.length === 0) return

    const targetIndexes = new Set(filteredRows.map((row) => row.originalIndex))
    if (targetIndexes.size === 0) return

    targetIndexes.forEach((index) => {
      form.setValue(`${name}[${index}].${checkboxFieldName}`, checked, {
        shouldDirty: true,
      })
    })
  }

  const hasCheckboxFields = Boolean(checkboxFieldName)
  const hasItems = controlledRows.length > 0
  const hasFilteredRows = filteredRows.length > 0
  const allFilteredChecked =
    hasFilteredRows &&
    filteredRows.every(({ item }) => getRowCheckboxValue(item))
  const someFilteredChecked = filteredRows.some(({ item }) =>
    getRowCheckboxValue(item),
  )
  const checkboxVariant =
    variant === 'default' || variant === 'error' ? variant : undefined

  const fieldArrayBaseId = name.replace(/[^a-zA-Z0-9_-]/g, '-')
  const searchInputId = `${fieldArrayBaseId}-search`
  const selectAllId = `${fieldArrayBaseId}-select-all`
  const errorId = `${fieldArrayBaseId}-error`
  const groupLabel = typeof field.label === 'string' ? field.label : name

  const fieldArrayErrorText = useMemo(() => {
    const error = getValueAtPath(form.formState.errors, field.name)
    if (!error) return null

    const friendlyRootMessage = getFriendlyFieldErrorMessage(
      field,
      error as any,
    )
    if (friendlyRootMessage) {
      return friendlyRootMessage
    }

    if (typeof error === 'object' && error && 'message' in error) {
      const message = (error as { message?: unknown }).message
      if (typeof message === 'string' && message) return message
    }

    if (Array.isArray(error)) {
      const messages = error
        .flatMap((rowError) => {
          if (!rowError || typeof rowError !== 'object') return []
          return Object.values(rowError)
            .map((entry) =>
              entry && typeof entry === 'object' && 'message' in entry
                ? (entry as { message?: unknown }).message
                : undefined,
            )
            .filter(
              (entry): entry is string => typeof entry === 'string' && !!entry,
            )
        })
        .filter(Boolean)

      if (messages.length > 0) {
        return messages.join(', ')
      }
    }

    return null
  }, [form.formState.errors, field.name])

  return (
    <fieldset
      className={cx(
        'col-span-12 space-y-4 border-t py-2 border-b',
        hideAddBtn && 'pb-4',
      )}
      aria-invalid={fieldArrayErrorText ? true : undefined}
      aria-describedby={fieldArrayErrorText ? errorId : undefined}
    >
      <legend className='col-span-12 text-sm font-normal text-slate-500'>
        {field.label}
        {field['aria-required'] ? ' *' : ''}
      </legend>

      {fieldsArraySearch && hasItems && (
        <div className='grid grid-cols-12 gap-4'>
          <div className='col-span-12'>
            <label htmlFor={searchInputId} className='sr-only'>
              {`Rechercher dans ${groupLabel}`}
            </label>
            <Input
              id={searchInputId}
              type='text'
              placeholder='Rechercher...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label={`Rechercher dans ${groupLabel}`}
              className='w-full max-w-xs'
            />
          </div>
        </div>
      )}

      {checkboxSelectAll && hasCheckboxFields && hasItems && (
        <div className='grid grid-cols-12 gap-4'>
          <div className='col-span-1 flex items-center justify-center'>
            <Checkbox
              id={selectAllId}
              name='select-all'
              onChange={(e) => handleSelectAll(e.target.checked)}
              checked={allFilteredChecked}
              indeterminate={
                hasFilteredRows && someFilteredChecked && !allFilteredChecked
              }
              variant={checkboxVariant}
            />
          </div>
          <div className='col-span-4 flex items-center'>
            <label
              htmlFor={selectAllId}
              className='text-sm font-medium text-slate-700 cursor-pointer'
            >
              Tout sélectionner
            </label>
          </div>
        </div>
      )}

      {filteredRows.length === 0 && hasItems && (
        <div
          className='col-span-12 text-sm text-slate-500 italic'
          role='status'
          aria-live='polite'
        >
          Aucun résultat trouvé
        </div>
      )}

      {filteredRows.map(({ originalIndex, rowId }) => {
        const indexToUse = originalIndex
        return (
          <div
            key={rowId}
            className='grid grid-cols-12 gap-4'
            role='group'
            aria-label={`${groupLabel} - ligne ${indexToUse + 1}`}
          >
            {arrayFields?.map((fieldItem) => {
              const renamedField = {
                ...fieldItem,
                name: `${name}[${indexToUse}].${fieldItem.name}`,
                // Already evaluated in FieldArray with the row scope.
                visibilityDependsOn: undefined,
              }

              if (fieldItem.visibilityDependsOn) {
                const dependentFieldPath = `${name}[${indexToUse}].${fieldItem.visibilityDependsOn}`
                const dependentValue = form.getValues(dependentFieldPath)

                if (!hasVisibleDependencyValue(dependentValue)) {
                  return null
                }
              }

              return (
                <DynamicFormFieldRender
                  key={renamedField.name}
                  field={renamedField}
                  form={form}
                  index={indexToUse}
                  variant={variant}
                  showError={false}
                />
              )
            })}
            {!hideRemoveBtn &&
              !arrayFields?.some(
                (f) => f.type === 'checkbox' || f.type === 'switch',
              ) && (
                <div className='col-span-1 flex items-end justify-center pb-2.5'>
                  <button
                    type='button'
                    onClick={() => remove(indexToUse)}
                    className='text-slate-500'
                    title='Supprimer'
                    aria-label={`Supprimer la ligne ${indexToUse + 1} de ${groupLabel}`}
                  >
                    <XIcon className=' size-6 text-slate-500' />
                  </button>
                </div>
              )}
          </div>
        )
      })}

      {fieldArrayErrorText && (
        <div
          id={errorId}
          role='alert'
          className='rounded-md border border-destructive/50 bg-destructive p-4 text-sm text-neutral-50'
        >
          {fieldArrayErrorText}
        </div>
      )}

      {!hideAddBtn && (
        <div className='col-span-12'>
          <Button
            type='button'
            variant='action'
            onClick={() => append({})}
            className='text-slate-500'
            aria-label={`Ajouter une ligne à ${groupLabel}`}
          >
            Ajouter
          </Button>
        </div>
      )}
    </fieldset>
  )
}
